import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

const PricingPage = () => {
  const [pdfPrices, setPdfPrices] = useState(null);
  const [pptPrices, setPptPrices] = useState(null);
  const [imageCosts, setImageCosts] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch PDF prices
  useEffect(() => {
    const fetchPdfPrices = async () => {
      try {
        const docRef = doc(db, "settings", "pdfCombinationPrices");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setPdfPrices(docSnap.data());
        }
      } catch (error) {
        console.error("Error fetching PDF prices:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPdfPrices();
  }, []);

  // Fetch PPT prices
  useEffect(() => {
    const fetchPptPrices = async () => {
      try {
        const docRef = doc(db, "settings", "pptCombinationPrices");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setPptPrices(docSnap.data());
        }
      } catch (error) {
        console.error("Error fetching PPT prices:", error);
      }
    };
    fetchPptPrices();
  }, []);

  // Fetch Image costs
  useEffect(() => {
    const fetchImageCosts = async () => {
      try {
        const docRef = doc(db, "settings", "imagePageCost");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setImageCosts(docSnap.data());
        }
      } catch (error) {
        console.error("Error fetching image costs:", error);
      }
    };
    fetchImageCosts();
  }, []);

  // Helper function to parse the price keys
  const parsePriceKey = (key) => {
    const parts = key.split("_");
    return {
      paperType: parts[0],
      size: parts[1],
      option: parts[2],
    };
  };

  // Group prices by paper type and size
  const groupPrices = (prices, type) => {
    if (!prices) return {};

    const grouped = {};

    // Get all color or blackWhite prices based on type
    const typePrices = prices[type === "ppt" ? "Black & White" : type] || {};

    Object.entries(typePrices).forEach(([key, value]) => {
      if (key === "updatedAt") return;

      const { paperType, size, option } = parsePriceKey(key);

      if (!grouped[paperType]) {
        grouped[paperType] = {};
      }

      if (!grouped[paperType][size]) {
        grouped[paperType][size] = {};
      }

      grouped[paperType][size][option] = value;
    });

    return grouped;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <h1 className="text-3xl font-bold text-center mb-8 text-gray-800">Service Pricing</h1>

      {/* PDF/Image Pricing Section */}
      <div className="mb-12 bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-blue-600 px-6 py-4">
          <h2 className="text-xl font-semibold text-white">PDF/Image Printing Prices</h2>
        </div>
        <div className="p-4">
          {pdfPrices ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paper Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      1 pg/sheet
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      2 pg/sheet
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      4 pg/sheet
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      6 pg/sheet
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      9 pg/sheet
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Object.entries(groupPrices(pdfPrices, "Black & White")).map(
                    ([paperType, sizes]) =>
                      Object.entries(sizes).map(([size, options]) => (
                        <tr key={`${paperType}-${size}`} className="hover:bg-gray-50">
                          {size === "A4" && (
                            <td
                              rowSpan="2"
                              className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 align-top"
                            >
                              {paperType.replace(/([A-Z])/g, " $1").trim()}
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {size}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            ₹{options["1"] || "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            ₹{options["2"] || "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            ₹{options["4"] || "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            ₹{options["6"] || "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            ₹{options["9"] || "-"}
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="mt-2">PDF pricing information not available</p>
            </div>
          )}
        </div>
      </div>

      {/* PPT Pricing Section */}
      <div className="mb-12 bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-green-600 px-6 py-4">
          <h2 className="text-xl font-semibold text-white">PPT Printing Prices</h2>
        </div>
        <div className="p-4">
          {pptPrices ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paper Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      1 option
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      2 options
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      4 options
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Object.entries(groupPrices(pptPrices, "ppt")).map(([paperType, sizes]) =>
                    Object.entries(sizes).map(([size, options]) => (
                      <tr key={`${paperType}-${size}`} className="hover:bg-gray-50">
                        {size === "A4" && (
                          <td
                            rowSpan="2"
                            className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 align-top"
                          >
                            {paperType.replace(/([A-Z])/g, " $1").trim()}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {size}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          ₹{options["1"] || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          ₹{options["2"] || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          ₹{options["4"] || "-"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="mt-2">PPT pricing information not available</p>
            </div>
          )}
        </div>
      </div>

      {/* Image Costs Section */}
      {imageCosts && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-12">
          <div className="bg-purple-600 px-6 py-4">
            <h2 className="text-xl font-semibold text-white">Image Costs</h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="bg-gray-800 p-2 rounded-full mr-3">
                  <svg
                    className="h-6 w-6 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-800">Black & White</h3>
              </div>
              <div className="space-y-2">
                <p className="text-gray-700 flex justify-between">
                  <span>A4:</span>
                  <span className="font-semibold">₹{imageCosts.blackAndWhite?.A4 || "-"}</span>
                </p>
                <p className="text-gray-700 flex justify-between">
                  <span>A3:</span>
                  <span className="font-semibold">₹{imageCosts.blackAndWhite?.A3 || "-"}</span>
                </p>
              </div>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="bg-red-500 p-2 rounded-full mr-3">
                  <svg
                    className="h-6 w-6 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-800">Color</h3>
              </div>
              <div className="space-y-2">
                <p className="text-gray-700 flex justify-between">
                  <span>A4:</span>
                  <span className="font-semibold">₹{imageCosts.color?.A4 || "-"}</span>
                </p>
                <p className="text-gray-700 flex justify-between">
                  <span>A3:</span>
                  <span className="font-semibold">₹{imageCosts.color?.A3 || "-"}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
